import { Plus } from 'lucide-react';
import React, { useState } from 'react';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../Context/authContext';
import { useCronJobs, useUpdateCronJob } from '../hooks';
import PageHeader from '../components/ui/PageHeader';
import AccentButton from '../components/ui/AccentButton';
import { CronJobForm } from '../components/cron-jobs/CronJobForm';
import { CronJobList } from '../components/cron-jobs/CronJobList';
import Toast from '../components/Toast';

const getScheduleValue = job => {
  if (job?.value != null) return String(job.value);

  const schedule = job?.schedule;
  if (!schedule) return '';
  if (schedule.kind === 'every') return String((schedule.everyMs || 0) / 1000);
  if (schedule.kind === 'after') return String((schedule.afterMs || schedule.everyMs || 0) / 1000);
  if (schedule.kind === 'cron') return schedule.expr || '';
  if (schedule.kind === 'at' && schedule.atMs) {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
      .format(new Date(schedule.atMs))
      .replace(/,/g, '');
  }
  return '';
};

const CronJobs = () => {
  const [editingJob, setEditingJob] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const { user } = useContextHook(AuthContext, v => ({
    user: v.user,
  }));

  const getAllJobs = useCronJobs();
  const updateJob = useUpdateCronJob();
  const jobs = getAllJobs?.data;

  const handleEditJob = job => {
    setEditingJob(job);
    setShowForm(true);
  };

  const handleCancel = () => {
    setEditingJob(null);
    setShowForm(false);
  };

  const handleCreate = () => {
    setEditingJob(null);
    setShowForm(true);
  };

  const handleToggleJob = job => {
    updateJob.mutate(
      {
        job_id: job?.id || job?.job_id,
        enable: !(job?.enabled ?? job?.enable),
        name: job?.name,
        message: job?.payload?.message || job?.message || '',
        user_id: user?.email,
        platform_id: 'plateslabs',
        schedule_type: job?.schedule?.kind || job?.schedule_type,
        value: getScheduleValue(job),
        action: {
          action_type: job?.action?.action_type || job?.action?.actionType,
          channel: job?.action?.channel,
        },
        emails: job?.emails || [],
      },
      {
        onSuccess: () => {
          Toast({ type: 'success', message: 'Task updated successfully' });
        },
        onError: err => {
          Toast({ type: 'error', message: err?.detail || err?.message || 'Failed to update task' });
        },
      },
    );
  };

  return (
    <div className="p-8 lg:p-10">
      <PageHeader
        title="Task Manager"
        subtitle="Schedule and manage recurring tasks for your agents."
        actions={
          <AccentButton onClick={handleCreate}>
            <Plus className="w-4 h-4" />
            New Task
          </AccentButton>
        }
      />

      {showForm && <CronJobForm onCancel={handleCancel} initialData={editingJob} setShowForm={setShowForm} />}

      <CronJobList jobs={jobs} loading={getAllJobs.isPending} onToggle={handleToggleJob} onEdit={handleEditJob} />
    </div>
  );
};

export default CronJobs;
